import { format, formatDistanceToNow, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { resolveCarrierPriceVisibility } from "../../util/freight/carrierPriceVisibility";
import {
  CONTRACTING_TYPE_BID,
  CONTRACTING_TYPE_FIXED,
  isBidFreight as isBidContractingType,
  resolveContractingTypeFromFreight,
} from "../../util/freight/contractingType";

export const PAGE_SIZE = 5;
/** Tamanho do lote ao buscar todos os fretes no servidor (paginação por cursor). */
export const FETCH_BATCH_SIZE = 20;
const MAX_FETCH_BATCHES = 100;

function unwrapDetail(response) {
  const root = response?.data;
  if (root?.data != null && typeof root.data === "object" && !Array.isArray(root.data)) {
    return root.data;
  }
  if (Array.isArray(root?.data) && root.data.length === 1) {
    return root.data[0];
  }
  if (root && typeof root === "object" && !Array.isArray(root) && root.id) {
    return root;
  }
  return root?.data ?? root;
}

function formatLocation(city, uf, fallback = "") {
  if (city && uf) return `${city}/${uf}`;
  return city || uf || fallback || "—";
}

function formatLocationFromPlace(place, stringFallback = "") {
  if (!place) return stringFallback || "—";
  if (typeof place === "string") return place;
  if (place.label) return place.label;
  return formatLocation(place.city, place.uf || place.state, stringFallback);
}

export function parseNumber(value, depth = 0) {
  if (value == null || value === "" || depth > 4) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "object") {
    return parseNumber(
      value.total ??
        value.value ??
        value.amount ??
        value.priceList ??
        value.netPrice,
      depth + 1,
    );
  }
  const cleaned = String(value)
    .trim()
    .replace(/R\$/gi, "")
    .replace(/[^\d,.-]/g, "");
  if (!cleaned) return null;
  const normalized = cleaned.includes(",")
    ? cleaned.replace(/\./g, "").replace(",", ".")
    : cleaned;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeContractType(item) {
  const raw =
    item.contractingType ??
    item.bidding?.contractingType ??
    item.contractType ??
    item.contract_type ??
    item.hiringType ??
    item.typeContract ??
    item.modality ??
    "";
  const text = String(raw).toLowerCase();
  if (text.includes("fixo") || text.includes("fixed") || text.includes("preco")) return "Preço Fixo";
  if (text.includes("bid") || text.includes("lance")) return "BID";
  if (item.type === "fixed" || item.isFixedPrice === true) return "Preço Fixo";
  if (item.type === "bid" || item.isBid === true) return "BID";
  return raw || "Preço Fixo";
}

function resolveFreightType(contractType) {
  const text = String(contractType).toLowerCase();
  if (text.includes("bid") || text.includes("lance")) return "bid";
  return "fixed";
}

export function isBidFreight(freight) {
  return isBidContractingType(freight) || freight?.type === "bid";
}

function formatPublishedAgo(item) {
  if (item.publishedAgo) return item.publishedAgo;
  const dateRaw =
    item.publishedAt ??
    item.published_at ??
    item.createdAt ??
    item.created_at ??
    item.releaseDate;
  if (!dateRaw) return "";
  try {
    const date = typeof dateRaw === "string" ? parseISO(dateRaw) : new Date(dateRaw);
    if (!isValid(date)) return "";
    return `Publicado ${formatDistanceToNow(date, { addSuffix: true, locale: ptBR })}`;
  } catch {
    return "";
  }
}

function parseCount(value) {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatFreightNumber(item) {
  if (item.numberSerial != null && item.numberSerial !== "") {
    return String(item.numberSerial);
  }

  const code = item.freightCode ?? item.number ?? item.code ?? item.serial ?? item.freightNumber;
  if (code == null || code === "") return "—";

  return String(code).replace(/^FT-/i, "");
}

function extractOrigin(item) {
  if (item.origin && typeof item.origin === "string") return item.origin;
  return formatLocationFromPlace(
    item.origin ?? item.firstDelivery,
    item.origem,
  );
}

function extractDestination(item) {
  if (item.destination && typeof item.destination === "string") return item.destination;
  return formatLocationFromPlace(
    item.destination ?? item.lastDelivery,
    item.destino,
  );
}

function extractVehicleType(item) {
  return (
    item.vehicleType ??
    item.vehicle_type ??
    item.typeVehicle ??
    selectedVehicleNames(item.vehicle?.typeVehicle) ??
    "—"
  );
}

function selectedVehicleNames(typeVehicle) {
  const items = Array.isArray(typeVehicle?.dados) ? typeVehicle.dados : [];
  const names = items.filter((i) => i?.selected).map((i) => i?.name).filter(Boolean);
  return names.length ? names.join(", ") : null;
}

function extractBodyType(item) {
  return (
    item.bodyworkType ??
    item.bodyType ??
    item.body_type ??
    item.carroceria ??
    selectedVehicleNames(item.vehicle?.typeBodyWork) ??
    "—"
  );
}

function extractCollectionDate(item) {
  const fromStop = Array.isArray(item?.stoppingPoints)
    ? item.stoppingPoints.find((stop) => {
        const ops = Array.isArray(stop?.operation)
          ? stop.operation
          : stop?.operation
            ? [stop.operation]
            : [];
        return ops.some((op) => String(op).toLowerCase() === "carga");
      })?.date_operation
    : "";

  return (
    item.collectionDate ??
    item.dateColeta ??
    item.firstDelivery?.dateColeta ??
    item.origin?.dateColeta ??
    fromStop ??
    item.stoppingPoints?.[0]?.date_operation ??
    ""
  );
}

/** Normaliza data de coleta para YYYY-MM-DD para comparação de filtro. */
export function normalizeCollectionDateKey(value) {
  if (value == null || value === "") return "";

  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    if (typeof seconds === "number") {
      const date = new Date(seconds * 1000);
      if (!Number.isNaN(date.getTime())) {
        return date.toISOString().slice(0, 10);
      }
    }
    if (typeof value.toDate === "function") {
      try {
        const date = value.toDate();
        if (date instanceof Date && !Number.isNaN(date.getTime())) {
          return date.toISOString().slice(0, 10);
        }
      } catch {
        /* ignore */
      }
    }
  }

  const raw = String(value).trim();
  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const brMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (brMatch) {
    const [, day, month, year] = brMatch;
    return `${year}-${month}-${day}`;
  }

  try {
    const date = parseISO(raw);
    if (isValid(date)) return format(date, "yyyy-MM-dd");
  } catch {
    /* ignore */
  }

  const fallback = new Date(raw);
  if (!Number.isNaN(fallback.getTime())) {
    return fallback.toISOString().slice(0, 10);
  }

  return "";
}

export function matchesCollectionDateFilter(freightCollectionDate, filterDate) {
  const filterKey = normalizeCollectionDateKey(filterDate);
  if (!filterKey) return true;
  const freightKey = normalizeCollectionDateKey(freightCollectionDate);
  if (!freightKey) return false;
  return freightKey === filterKey;
}

function extractDeliveryDate(item) {
  return (
    item.deliveryDate ??
    item.dateEntrega ??
    item.lastDelivery?.dateEntrega ??
    item.lastDelivery?.dateColeta ??
    ""
  );
}

function formatWeight(item) {
  if (typeof item?.weight === "string" && item.weight.trim()) {
    const trimmed = item.weight.trim();
    if (/[a-zA-Z]/.test(trimmed)) return trimmed;
  }
  const raw = item.weightCargo ?? item.weight ?? item.peso ?? item.weightKg;
  if (raw == null || raw === "") return "—";
  const num = Number(raw);
  if (!Number.isFinite(num)) return String(raw);
  return `${num.toLocaleString("pt-BR")} kg`;
}

export function formatDistanceKm(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "";
  return `${Math.round(num).toLocaleString("pt-BR")} km`;
}

export function parseDistanceKm(item) {
  return parseNumber(
    item?.distanceKm ??
      item?.distancia_km ??
      item?.totalAmountRoute ??
      item?.freight?.totalAmountRoute,
  );
}

function formatBidTime(value) {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{2}:\d{2}$/.test(value)) return value;
  try {
    const date = typeof value === "string" ? parseISO(value) : new Date(value);
    if (!isValid(date)) return String(value);
    return format(date, "HH:mm");
  } catch {
    return String(value);
  }
}

function asNonEmptyString(value) {
  if (value == null || value === "") return "";
  const str = String(value).trim();
  return str || "";
}

function extractShipperId(item) {
  if (!item || typeof item !== "object") return "";

  const clientPayment =
    item.clientPayment ?? item.client_payment ?? item.client ?? {};

  const pagadorRaw = item.pagador ?? item.freight?.pagador;
  const pagadorUid =
    typeof pagadorRaw === "string"
      ? pagadorRaw
      : pagadorRaw?.uid ?? pagadorRaw?.id ?? "";

  const shipperObj = item.shipper;
  const shipperUid =
    shipperObj && typeof shipperObj === "object"
      ? shipperObj.uid ?? shipperObj.id ?? ""
      : "";

  return (
    asNonEmptyString(item.idShipper) ||
    asNonEmptyString(clientPayment?.uid) ||
    asNonEmptyString(clientPayment?.id) ||
    asNonEmptyString(pagadorUid) ||
    asNonEmptyString(item.shipperUid) ||
    asNonEmptyString(item.uidShipperPayment) ||
    asNonEmptyString(item.shipperId) ||
    asNonEmptyString(item.idShipperPayment) ||
    asNonEmptyString(shipperUid) ||
    ""
  );
}

function extractCarrierQueueStatus(item) {
  if (!item || typeof item !== "object") {
    return { isInCarrierQueue: false, joinedAt: null };
  }

  const carrierQueue =
    item.carrierQueue && typeof item.carrierQueue === "object"
      ? item.carrierQueue
      : item.transportQueue && typeof item.transportQueue === "object"
        ? item.transportQueue
        : {};

  const rawFlag =
    item.isInCarrierQueue ??
    item.inCarrierQueue ??
    carrierQueue.inQueue ??
    carrierQueue.isInQueue ??
    false;

  const isInCarrierQueue =
    rawFlag === true ||
    rawFlag === 1 ||
    String(rawFlag).toLowerCase() === "true" ||
    String(rawFlag).toUpperCase() === "IN_QUEUE";

  const joinedAt =
    carrierQueue.dataHora ??
    carrierQueue.joinedAt ??
    carrierQueue.registeredAt ??
    item.carrierQueueJoinedAt ??
    null;

  return {
    isInCarrierQueue,
    joinedAt,
    carrierQueue: isInCarrierQueue ? carrierQueue : null,
  };
}

function extractShipper(item) {
  return (
    item.clientPaymentName ??
    item.shipperName ??
    item.shipper ??
    item.clientPayment?.name ??
    item.client?.name ??
    item.embarcador ??
    "—"
  );
}

function extractObservations(item) {
  // Preferência: Observações Motorista (freight.observation)
  const driverObservation =
    (typeof item?.observation === "string" && item.observation.trim()) ||
    (typeof item?.freight?.observation === "string" &&
      item.freight.observation.trim()) ||
    "";
  if (driverObservation) return [driverObservation];

  if (Array.isArray(item.observations) && item.observations.length) {
    return item.observations.filter(Boolean);
  }
  if (Array.isArray(item.observacoes) && item.observacoes.length) {
    return item.observacoes.filter(Boolean);
  }
  if (typeof item.observations === "string" && item.observations.trim()) {
    return [item.observations.trim()];
  }
  return [];
}

function extractMerchandiseDescription(item) {
  if (!item || typeof item !== "object") return null;

  const fromApi =
    item.merchandiseDescription ??
    item.merchandise_description ??
    item.cargoDescription ??
    item.descricaoMercadoria;
  if (typeof fromApi === "string" && fromApi.trim()) {
    return fromApi.trim();
  }

  const stops = Array.isArray(item.stopping_points)
    ? item.stopping_points
    : Array.isArray(item.stops)
      ? item.stops
      : [];

  const isCargoStop = (point) => {
    if (point?.typeCarga === true) return true;
    const operations = Array.isArray(point?.operation)
      ? point.operation
      : point?.operation
        ? [String(point.operation)]
        : [];
    return operations.some((op) =>
      String(op || "")
        .toLowerCase()
        .includes("carga"),
    );
  };

  const fromCargo = stops
    .filter(isCargoStop)
    .map((p) => String(p?.descricao || "").trim())
    .filter(Boolean);
  const uniqueCargo = [...new Set(fromCargo)];
  if (uniqueCargo.length) return uniqueCargo.join(", ");

  const fromAny = stops
    .map((p) => String(p?.descricao || "").trim())
    .filter(Boolean);
  const uniqueAny = [...new Set(fromAny)];
  return uniqueAny.length ? uniqueAny.join(", ") : null;
}

function asProductList(value) {
  if (typeof value === "string") {
    try {
      return asProductList(JSON.parse(value));
    } catch {
      return [];
    }
  }
  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === "object");
  }
  if (value && typeof value === "object") {
    return Object.values(value).filter(
      (item) => item && typeof item === "object" && !Array.isArray(item),
    );
  }
  return [];
}

function resolveProductDisplayName(value, depth = 0) {
  if (value == null || depth > 3) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    const nested =
      value.nameProducts ??
      value.value ??
      value.label ??
      value.name ??
      value.product ??
      value.descricao;
    if (nested != null && nested !== value) {
      const resolved = resolveProductDisplayName(nested, depth + 1);
      if (resolved) return resolved;
    }
  }
  return "";
}

function getPricingProducts(item = {}) {
  const freight =
    item?.freight && typeof item.freight === "object" ? item.freight : {};

  const fromBlocks = [
    item?.newProducts,
    freight?.newProducts,
    item?.products,
    item?.pricing?.products,
    freight?.products,
    freight?.freight?.newProducts,
    item?.freight?.freight?.newProducts,
  ];

  const products = [];
  const seen = new Set();
  for (const block of fromBlocks) {
    for (const entry of asProductList(block)) {
      const key = [
        resolveProductDisplayName(entry),
        String(entry?.amount ?? ""),
        String(entry?.total ?? ""),
        String(entry?.priceList ?? ""),
        String(entry?.totalPrice ?? ""),
        String(entry?.value ?? ""),
      ].join("|");
      if (seen.has(key)) continue;
      seen.add(key);
      products.push(entry);
    }
  }
  return products;
}

function extractFreteProduct(item) {
  const products = getPricingProducts(item);

  return (
    products.find((product) =>
      productNameMatches(product, ["frete"]),
    ) ?? products[0] ?? null
  );
}

function normalizeProductName(value) {
  return resolveProductDisplayName(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function compactProductName(value) {
  return normalizeProductName(value).replace(/[^a-z0-9]/g, "");
}

function productNameMatches(name, wantedNames = []) {
  const normalized = normalizeProductName(name);
  const compact = compactProductName(name);
  if (!compact) return false;
  if (wantedNames.some((wanted) => compactProductName(wanted) === "pedagio")) {
    if (compact.includes("pedag")) return true;
  }
  return wantedNames.some((wanted) => {
    const token = normalizeProductName(wanted);
    const compactToken = compactProductName(wanted);
    return Boolean(
      (token && (normalized === token || normalized.includes(token))) ||
        (compactToken && compact.includes(compactToken)),
    );
  });
}

export function resolveTollAmountFromFreight(item) {
  return firstPositiveNumber(
    extractNamedProductTotal(item, ["pedagio", "vale pedagio"]),
    item?.tollAmount,
    item?.toll,
    item?.pedagio,
  );
}

function extractNamedProductTotal(item, names = []) {
  const products = getPricingProducts(item);
  const product = products.find((entry) => productNameMatches(entry, names));
  if (!product) return null;
  const amount = parseNumber(product.amount);
  const priceList = parseNumber(product.priceList);
  const computed =
    amount != null && amount > 0 && priceList != null && priceList > 0
      ? amount * priceList
      : null;
  return firstPositiveNumber(
    product.total,
    product.netPrice,
    product.totalPrice,
    product.value,
    product.valor,
    product.priceList,
    computed,
  );
}

function firstPositiveNumber(...values) {
  for (const value of values) {
    const parsed = parseNumber(value);
    if (parsed != null && parsed > 0) return parsed;
  }
  return null;
}

function normalizePricingItem(item = {}) {
  const freight =
    item?.freight && typeof item.freight === "object" ? item.freight : {};
  const values =
    item?.values && typeof item.values === "object"
      ? item.values
      : freight?.values && typeof freight.values === "object"
        ? freight.values
        : {};
  const bidding =
    item?.bidding && typeof item.bidding === "object"
      ? item.bidding
      : freight?.bidding && typeof freight.bidding === "object"
        ? freight.bidding
        : {};
  const freteProduct = extractFreteProduct({ ...item, freight });

  return {
    ...item,
    freight,
    values,
    bidding,
    pricingType:
      item?.pricingType ??
      freight?.pricingType ??
      values?.pricingType ??
      bidding?.pricingType ??
      freteProduct?.pricingType,
    pricePerTon:
      item?.pricePerTon ??
      freight?.pricePerTon ??
      values?.pricePerTon ??
      bidding?.pricePerTon ??
      freteProduct?.priceList,
    fixedPriceAmount:
      item?.fixedPriceAmount ??
      freight?.fixedPriceAmount ??
      values?.fixedPriceAmount ??
      bidding?.fixedPriceAmount,
    valueFreightage:
      item?.valueFreightage ??
      freight?.valueFreightage ??
      values?.valueFreightage ??
      item?.freightValue ??
      freteProduct?.total ??
      freteProduct?.netPrice,
    newProducts: getPricingProducts({ ...item, freight }),
  };
}

function extractPricingType(item) {
  const normalizedItem = normalizePricingItem(item);
  const rootType =
    normalizedItem?.pricingType ??
    normalizedItem?.freight?.pricingType ??
    normalizedItem?.values?.pricingType ??
    normalizedItem?.bidding?.pricingType;
  if (rootType != null && rootType !== "") {
    return String(rootType).trim();
  }

  const freteProduct = extractFreteProduct(normalizedItem);
  if (freteProduct?.pricingType != null && freteProduct.pricingType !== "") {
    return String(freteProduct.pricingType).trim();
  }

  return "Total";
}

function extractValueFreightage(item) {
  const normalizedItem = normalizePricingItem(item);
  const freteProduct = extractFreteProduct(normalizedItem);

  return parseNumber(
    normalizedItem?.valueFreightage ??
      normalizedItem?.freight?.valueFreightage ??
      normalizedItem?.values?.valueFreightage ??
      freteProduct?.total ??
      freteProduct?.netPrice,
  );
}

function extractPricePerTon(item) {
  const normalizedItem = normalizePricingItem(item);
  const freteProduct = extractFreteProduct(normalizedItem);

  return parseNumber(
    freteProduct?.priceList ??
      normalizedItem?.pricePerTon ??
      normalizedItem?.freight?.pricePerTon ??
      normalizedItem?.values?.pricePerTon ??
      normalizedItem?.bidding?.pricePerTon ??
      normalizedItem?.fixedPriceAmount ??
      normalizedItem?.freight?.fixedPriceAmount ??
      normalizedItem?.bidding?.fixedPriceAmount,
  );
}

function isTonPricingType(pricingType) {
  return String(pricingType || "").trim().toLowerCase() === "ton";
}

function resolveFreightDisplayValue(item) {
  const normalizedItem = normalizePricingItem(item);
  const pricingType = extractPricingType(normalizedItem);

  if (isTonPricingType(pricingType)) {
    return {
      freightValue: extractPricePerTon(normalizedItem),
      pricingType: "Ton",
    };
  }

  return {
    freightValue: extractValueFreightage(normalizedItem),
    pricingType: pricingType || "Total",
  };
}

export { extractFreteProduct, isTonPricingType, resolveFreightDisplayValue };

function mapBidHistoryItem(bid, index) {
  return {
    position: bid.position ?? bid.rank ?? bid.posicao ?? index + 1,
    carrier:
      bid.carrierName ??
      bid.carrier ??
      bid.transportadora ??
      bid.companyName ??
      "—",
    amount: parseNumber(bid.amount ?? bid.value ?? bid.lance ?? bid.bidValue) ?? 0,
    time: formatBidTime(bid.submittedAt ?? bid.time ?? bid.hour ?? bid.horario ?? bid.createdAt),
  };
}

function extractBidHistory(item) {
  const history =
    item.bidding?.bidHistory ??
    item.bidHistory ??
    item.bidsHistory ??
    item.bids ??
    item.lances ??
    item.topBids ??
    [];
  if (!Array.isArray(history)) return [];
  return history.slice(0, 5).map(mapBidHistoryItem);
}

function mapBaseFields(item, options = {}) {
  const id = item.id ?? item._id ?? item.freightId;
  const bidding = item.bidding ?? {};
  const dispute = bidding.dispute ?? {};
  const yourBid = bidding.yourBid ?? {};
  const contractType = normalizeContractType(item);
  const type = resolveFreightType(contractType);

  const bestBid = parseNumber(
    item.bestBidAmount ??
      dispute.bestBidAmount ??
      item.bestBid ??
      item.best_bid ??
      item.currentBestBid ??
      item.lowestBid,
  );

  const { freightValue: displayFreightValue, pricingType } =
    resolveFreightDisplayValue(item);

  const priceVisibility = resolveCarrierPriceVisibility(item, {
    viewerShipperIds: options.viewerShipperIds,
    totalPrice: displayFreightValue,
    pricingType,
    fallbackShipperName: extractShipper(item),
  });

  const participants =
    item.participantsCount ??
    dispute.participantsCount ??
    item.participants ??
    item.bidCount ??
    0;

  const userPosition =
    yourBid.position ??
    item.userPosition ??
    item.userRank ??
    item.myPosition ??
    null;

  const carrierQueueStatus = extractCarrierQueueStatus(item);

  return {
    id,
    number: formatFreightNumber(item),
    type,
    origin: extractOrigin(item),
    destination: extractDestination(item),
    vehicleType: extractVehicleType(item),
    bodyType: extractBodyType(item),
    collectionDate: extractCollectionDate(item),
    contractType,
    bestBid: bestBid ?? 0,
    freightValue: priceVisibility.displayPrice ?? 0,
    pricingType,
    participants,
    publishedAgo: formatPublishedAgo(item),
    shipper: priceVisibility.displayShipperName,
    idShipper: extractShipperId(item),
    weight: formatWeight(item),
    distanceKm: parseDistanceKm(item),
    volume: item.volume ?? item.cubagem ?? (item.volumeM3 ? `${item.volumeM3} m³` : "—"),
    merchandiseDescription: extractMerchandiseDescription(item),
    deliveryDate: extractDeliveryDate(item),
    modality: item.contractingLabel ?? bidding.contractingLabel ?? item.modality ?? contractType,
    observations: extractObservations(item),
    observation:
      (typeof item.observation === "string" && item.observation.trim()) ||
      (typeof item.freight?.observation === "string" &&
        item.freight.observation.trim()) ||
      null,
    contractingType:
      resolveContractingTypeFromFreight(item) ||
      (type === "bid" ? CONTRACTING_TYPE_BID : CONTRACTING_TYPE_FIXED),
    bidHistory: extractBidHistory(item),
    userPosition,
    yourBidAmount: parseNumber(item.yourBidAmount ?? yourBid.amount),
    canSubmitBid:
      typeof item.canSubmitBid === "boolean"
        ? item.canSubmitBid
        : yourBid.canSubmit !== false,
    counterAmount: parseNumber(item.counterAmount),
    replyAmount: parseNumber(item.replyAmount),
    negotiationTurn: item.negotiationTurn ?? null,
    negotiationStatusLabel: item.negotiationStatusLabel ?? null,
    canAcceptCounter: item.canAcceptCounter === true,
    canReplyBid: item.canReplyBid === true,
    bidSubmittedAt: item.bidSubmittedAt ?? item.bidSubmittedAtIso ?? null,
    paymentTermLabel:
      item.paymentTermLabel ?? item.paymentTerm ?? item.prazoPagamento ?? null,
    bidPaymentTermDays: parseNumber(
      item.bidPaymentTermDays ?? item.paymentTermDays,
    ),
    referenceValue:
      parseNumber(item.referenceValue) ?? displayFreightValue,
    tollAmount: firstPositiveNumber(
      extractNamedProductTotal(item, ["pedagio", "vale pedagio"]),
      item.tollAmount,
      item.toll,
      item.pedagio,
      item.freight?.pedagio,
      item.values?.toll,
      item.values?.pedagio,
      item.simulation?.pedagio,
    ),
    anttValue: firstPositiveNumber(
      item.anttValue,
      extractNamedProductTotal(item, ["frete"]),
      item.priceANTT,
      item.freight?.priceANTT,
      item.values?.priceANTT,
    ),
    isInCarrierQueue: carrierQueueStatus.isInCarrierQueue,
    carrierQueueJoinedAt: carrierQueueStatus.joinedAt,
    carrierQueue: carrierQueueStatus.carrierQueue,
    priceVisibility,
    isMasked: priceVisibility.isMasked === true,
    newProducts: getPricingProducts(item),
  };
}

export function mapAvailableListItem(item, options = {}) {
  const mapped = mapBaseFields(item, options);
  return {
    id: mapped.id,
    number: mapped.number,
    type: mapped.type,
    contractingType: mapped.contractingType,
    contractingLabel: item.contractingLabel ?? mapped.modality,
    yourBidAmount: mapped.yourBidAmount,
    canSubmitBid: mapped.canSubmitBid,
    counterAmount: mapped.counterAmount,
    replyAmount: mapped.replyAmount,
    negotiationTurn: mapped.negotiationTurn,
    negotiationStatusLabel: mapped.negotiationStatusLabel,
    canAcceptCounter: mapped.canAcceptCounter,
    canReplyBid: mapped.canReplyBid,
    bidSubmittedAt: mapped.bidSubmittedAt,
    paymentTermLabel: mapped.paymentTermLabel,
    bidPaymentTermDays: mapped.bidPaymentTermDays,
    referenceValue: mapped.referenceValue,
    tollAmount: mapped.tollAmount,
    anttValue: mapped.anttValue,
    origin: mapped.origin,
    destination: mapped.destination,
    vehicleType: mapped.vehicleType,
    bodyType: mapped.bodyType,
    collectionDate: mapped.collectionDate,
    contractType: mapped.contractType,
    bestBid: mapped.bestBid,
    freightValue: mapped.freightValue,
    pricingType: mapped.pricingType,
    participants: mapped.participants,
    publishedAgo: mapped.publishedAgo,
    publishedAt: item.publishedAt ?? mapped.publishedAgo ?? null,
    shipper: mapped.shipper,
    idShipper: mapped.idShipper,
    isInCarrierQueue: mapped.isInCarrierQueue,
    carrierQueueJoinedAt: mapped.carrierQueueJoinedAt,
    carrierQueue: mapped.carrierQueue,
    priceVisibility: mapped.priceVisibility,
    isMasked: mapped.isMasked === true,
    merchandiseDescription: mapped.merchandiseDescription,
    observations: mapped.observations,
    observation: mapped.observation,
    weight: mapped.weight,
    distanceKm: mapped.distanceKm,
    cargoType: item.cargoType ?? item.tipoCarga ?? mapped.merchandiseDescription,
    newProducts: mapped.newProducts,
  };
}

export function mapAvailableDetail(item, options = {}) {
  return mapBaseFields(item, options);
}

export function buildAvailableQueryParams(
  filters,
  { limit = PAGE_SIZE, cursor, idCarrier } = {},
) {
  const params = { limit };

  if (cursor) params.cursor = cursor;
  if (idCarrier) params.idCarrier = idCarrier;

  const numberFilter = filters.number?.trim();
  if (numberFilter) {
    const normalized = numberFilter.replace(/^FT-/i, "").trim();
    if (/^\d+$/.test(normalized)) {
      params.numberSerial = normalized;
    } else {
      params.freightCode = numberFilter;
    }
  }

  if (filters.origin?.trim()) {
    params.origem = filters.origin.trim().replace(/\s*-\s*/g, "/");
  }
  if (filters.destination?.trim()) {
    params.destino = filters.destination.trim().replace(/\s*-\s*/g, "/");
  }
  if (filters.vehicleType) params.vehicleType = filters.vehicleType;
  if (filters.bodyType) params.bodyworkType = filters.bodyType;
  if (filters.collectionDate) params.collectionDate = filters.collectionDate;

  params.contractingType = "FIXED";

  return params;
}

export function parseAvailableListResponse(response, limit, cursorForPage) {
  const root = response?.data ?? {};
  const items = Array.isArray(root.data)
    ? root.data
    : Array.isArray(root?.items)
      ? root.items
      : [];

  const pagination = root.pagination ?? {};
  const nextCursor = pagination.nextCursor ?? pagination.next_cursor ?? null;
  const serverHasMore = pagination.hasMore === true || pagination.has_more === true;
  const total = parseCount(root.count) ??
    parseCount(pagination.total) ??
    parseCount(pagination.totalCount) ??
    parseCount(pagination.count);

  const hasValidNextCursor =
    typeof nextCursor === "string" &&
    nextCursor.trim().length > 0 &&
    nextCursor !== cursorForPage;
  const hasMore = serverHasMore && hasValidNextCursor;

  return {
    items,
    nextCursor,
    hasMore,
    serverHasMore,
    total,
  };
}

export function getApiErrorMessage(error, fallback = "Ocorreu um erro inesperado.") {
  return (
    error?.response?.data?.message ??
    error?.response?.data?.msg ??
    error?.response?.data?.error ??
    error?.message ??
    fallback
  );
}

export function getShipperDisplayName(shipper, fallback = "") {
  const fantasyName = String(shipper?.dataPersonal?.nameFantasy || "").trim();
  const socialName = String(shipper?.dataPersonal?.socialName || "").trim();
  return fantasyName || socialName || fallback;
}

export function buildSubmitBidPayload(user, shipper, amount) {
  const base = buildEnterQueuePayload(user, shipper);
  const parsedAmount = Number(amount);
  if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
    throw new Error("Informe um valor de lance válido.");
  }
  return {
    ...base,
    amount: parsedAmount,
    contractingType: CONTRACTING_TYPE_BID,
  };
}

export function buildEnterQueuePayload(user, shipper) {
  const idCarrier = user?.uidShipper;
  if (!idCarrier) {
    throw new Error("Usuário sem transportadora vinculada.");
  }

  const nameCarrier = getShipperDisplayName(shipper, user?.name || "");
  if (!nameCarrier) {
    throw new Error("Nome da transportadora não informado.");
  }

  const idUser = user?.uid || "";
  if (!idUser) {
    throw new Error("Usuário não identificado.");
  }

  return {
    idCarrier,
    nameCarrier,
    idUser,
  };
}

export function buildLeaveQueuePayload(user) {
  const idCarrier = user?.uidShipper;
  if (!idCarrier) {
    throw new Error("Usuário sem transportadora vinculada.");
  }

  const idUser = user?.uid || "";
  if (!idUser) {
    throw new Error("Usuário não identificado.");
  }

  return { idCarrier, idUser };
}

export function formatCarrierQueueWaitingTime(freight) {
  const joinedAt =
    freight?.carrierQueueJoinedAt ??
    freight?.carrierQueue?.dataHora ??
    freight?.carrierQueue?.joinedAt ??
    freight?.carrierQueue?.registeredAt ??
    null;

  if (!joinedAt) return null;

  try {
    const date =
      typeof joinedAt === "string"
        ? parseISO(joinedAt)
        : joinedAt instanceof Date
          ? joinedAt
          : new Date(joinedAt);

    if (!isValid(date)) return null;

    return formatDistanceToNow(date, { locale: ptBR });
  } catch {
    return null;
  }
}

export function buildAcceptingClientPayment(user, shipper) {
  const uid = user?.uidShipper;
  if (!uid) {
    throw new Error("Usuário sem embarcador vinculado.");
  }

  return {
    uid,
    name: getShipperDisplayName(shipper, user?.name || ""),
    cnpj: shipper?.dataPersonal?.documentNumber || "",
  };
}

export function unwrapAvailableDetail(response) {
  return unwrapDetail(response);
}

/** Busca todos os fretes disponíveis percorrendo a paginação por cursor do backend. */
export async function fetchAllAvailableFreights(
  getAvailableFreights,
  filters,
  { idCarrier } = {},
) {
  const byId = new Map();
  let cursor;
  let totalFromApi = null;

  for (let batch = 0; batch < MAX_FETCH_BATCHES; batch += 1) {
    const params = buildAvailableQueryParams(filters, {
      limit: FETCH_BATCH_SIZE,
      cursor,
      idCarrier,
    });

    const response = await getAvailableFreights(params);
    const parsed = parseAvailableListResponse(response, FETCH_BATCH_SIZE, cursor);

    if (parsed.total != null) totalFromApi = parsed.total;

    parsed.items.forEach((item) => {
      const id = item.id ?? item._id;
      if (id) byId.set(id, item);
    });

    if (!parsed.serverHasMore || !parsed.nextCursor) break;
    if (parsed.nextCursor === cursor) break;
    cursor = parsed.nextCursor;
  }

  return {
    items: Array.from(byId.values()),
    total: totalFromApi ?? byId.size,
  };
}
